import { HeaderText } from '@/components/HeaderText';
import { UploadStatusModal } from '@/components/modals/UploadStatusModal/UploadStatusModal';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';
import { FileMap } from '@/types/upload';
import { Close } from '@mui/icons-material';
import { Box, Drawer, IconButton, List } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import {
  UploadStatusTabs,
  getUploadFileCategories,
  calculateUploadProgress,
} from '@/components/Upload/utils/uploadUtils';
import { UploadHeaderProgress } from '@/components/Upload/UploadDrawer/UploadHeaderProgress';
import { UploadFileListItem } from '@/components/Upload/UploadDrawer/UploadFileListItem';
import { pluralize } from '@/utils/format';

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.surface,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledList = styled(List)({
  maxHeight: '300px',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 0,
});

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    position: 'fixed !important',
    bottom: '0 !important',
    height: 'auto !important',
    maxHeight: '60vh !important',
    borderRadius: '8px 8px 0 0 !important',
    border: 'none !important',
    boxShadow: theme.shadows[8],
    margin: '0 16px',
    [theme.breakpoints.up('sm')]: {
      right: '82px !important',
      left: 'auto !important',
      width: '480px !important',
    },
  },
}));

export const UploadDrawer = ({
  files,
  uploadProgress,
  isOpen,
  onClose,
}: {
  files: FileMap;
  uploadProgress: Record<string, number>;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusModalFilter, setStatusModalFilter] = useState<UploadStatusTabs>(UploadStatusTabs.ALL);

  const fileArray = Object.values(files);
  const {
    completed: completedFiles,
    uploading: uploadingFiles,
    failed: failedFiles,
    queued: queuedFiles,
    progressEligible: progressEligibleFiles,
  } = getUploadFileCategories(files);
  const overallProgress = calculateUploadProgress(progressEligibleFiles, uploadProgress);

  const getSummaryText = () => {
    if (uploadingFiles.length > 0) {
      return `${uploadingFiles.length} uploading`;
    } else if (completedFiles.length > 0) {
      return `Uploading complete`;
    } else {
      return pluralize(fileArray.length, 'file', 'files');
    }
  };

  if (!isOpen || fileArray.length === 0) {
    return;
  }

  const openStatusModal = (filter: UploadStatusTabs) => {
    setStatusModalFilter(filter);
    setIsStatusModalOpen(true);
  };

  const closeStatusModal = () => setIsStatusModalOpen(false);

  return (
    <StyledDrawer
      variant='persistent'
      anchor='bottom'
      open={isOpen}
      onClose={onClose}
      data-testid={UploadTestIds.UploadDrawer}
    >
      <Header>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}
        >
          <HeaderText data-testid={UploadTestIds.UploadSummary}>{getSummaryText()}</HeaderText>
          <UploadHeaderProgress
            overallProgress={overallProgress}
            uploadingFiles={uploadingFiles}
            completedFiles={completedFiles}
            failedFiles={failedFiles}
            openStatusModal={openStatusModal}
          />
        </Box>
        <IconButton size='small' onClick={onClose} data-testid={UploadTestIds.UploadCloseButton}>
          <Close fontSize='small' />
        </IconButton>
      </Header>
      <StyledList data-testid={UploadTestIds.UploadList}>
        {fileArray.map((file, index) => (
          <UploadFileListItem key={`${file.path}-${index}`} file={file} uploadProgress={uploadProgress} index={index} />
        ))}
      </StyledList>
      <UploadStatusModal
        open={isStatusModalOpen}
        onClose={closeStatusModal}
        completedFiles={completedFiles}
        failedFiles={failedFiles}
        queuedFiles={queuedFiles}
        initialFilter={statusModalFilter}
      />
    </StyledDrawer>
  );
};
